/**
 * Staff S1 (AB#2204) — strings for the staff access batch: the staff booking
 * detail, owner staff management (deactivation warning), the login message for
 * removed staff access and the invitation errors. Staff S2 (AB#2205) — My Day,
 * My Schedule, staff identity and staff booking notifications. Merged into
 * the runtime dictionary by i18n/owner/index.ts and read through
 * LanguageContext's t(). Every key needs BOTH an en and an ar value.
 *
 * Keys are prefixed `stf.`.
 */
import type { OwnerStrings } from './types';

export const staffStrings: OwnerStrings = {
    en: {
        /* ── Staff booking detail (SG-05) ───────────────────────── */
        'stf.detail.notes': 'Booking notes',
        'stf.detail.loadFailed': 'Couldn’t load the latest booking details. Showing what the list has.',

        /* ── Deactivation (SG-11) ───────────────────────────────── */
        'stf.deactivate.futureBookingsOne': '{name} is now inactive. 1 upcoming booking stays assigned to them — reassign or cancel it from the Calendar.',
        'stf.deactivate.futureBookingsMany': '{name} is now inactive. {count} upcoming bookings stay assigned to them — reassign or cancel them from the Calendar.',

        /* ── Login / session (SG-11) ────────────────────────────── */
        'stf.login.accessRemoved': 'Your access to this salon has been removed. Contact the salon owner if you think this is a mistake.',

        /* ── Invitation acceptance (SG-10) ──────────────────────── */
        'stf.invite.accountExists': 'An account with this email or phone number already exists, so this invitation can’t create a new one. Ask the salon owner to invite you with different contact details.',
        'stf.invite.invalid': 'This invitation link is no longer valid. It may have expired or been withdrawn — ask the salon owner to send a new one.',
        'stf.invite.missingToken': 'This invitation link is incomplete. Open the link from your invitation email again.',
        'stf.invite.acceptFailed': 'Couldn’t accept the invitation. Please try again.',

        /* ── S2 (AB#2205) — shared ── */
        'stf.loading': 'Loading…',
        'stf.retry': 'Try again',
        'stf.detail.unavailable': 'This booking isn’t available to you any more.',
        'stf.absence.other': 'Time off',
        'stf.nav.myDay': 'My day',
        'stf.nav.mySchedule': 'My schedule',

        /* ── Identity (SG-17) ── */
        'stf.role.staff': 'Staff',
        'stf.identity.roleAtSalon': '{role} · {salon}',
        'stf.identity.allBranches': 'All branches',
        'stf.profile.workplace': 'Workplace',
        'stf.profile.readOnly': 'Set by the salon owner.',
        'stf.profile.salon': 'Salon',
        'stf.profile.branches': 'Branches',
        'stf.profile.position': 'Position',
        'stf.profile.role': 'Role',

        /* ── My Day (SG-06) ── */
        'stf.myDay.greeting': 'Hello, {name}',
        'stf.myDay.greetingNoName': 'Hello',
        'stf.myDay.loadFailed': 'Couldn’t load your day. Check your connection and try again.',
        'stf.shift.title': 'Today’s shift',
        'stf.shift.working': 'Working today',
        'stf.shift.off': 'Day off',
        'stf.shift.onLeave': 'On leave: {type}',
        'stf.shift.awayBetween': 'Away {range}',
        'stf.shift.unknown': 'Your shift isn’t available yet.',
        'stf.shift.salonClosed': 'Salon closed today',
        'stf.next.title': 'Next appointment',
        'stf.next.none': 'No more appointments today.',
        'stf.next.open': 'Open booking',
        'stf.pending.title': 'Needs your attention',
        'stf.pending.none': 'Nothing waiting for you.',
        'stf.pending.awaitingOne': '1 booking is awaiting confirmation',
        'stf.pending.awaitingMany': '{count} bookings are awaiting confirmation',
        'stf.pending.review': 'Review requests',
        'stf.pending.unpaidOne': '1 completed booking isn’t paid yet',
        'stf.pending.unpaidMany': '{count} completed bookings aren’t paid yet',
        'stf.pending.markPaidFor': 'Mark as paid: {time} {service}',
        'stf.pending.seeUnpaid': 'See unpaid bookings',
        'stf.today.title': 'Today’s appointments',
        'stf.today.empty': 'No appointments today.',
        'stf.today.nextTag': 'Next',
        'stf.today.openNamed': 'Open booking at {time}: {service}',
        'stf.counts.label': 'Today at a glance',
        'stf.counts.pending': 'Awaiting confirmation: {count}',
        'stf.counts.confirmed': 'Confirmed: {count}',
        'stf.counts.inVisit': 'In visit: {count}',
        'stf.counts.completed': 'Completed: {count}',
        'stf.counts.cancelled': 'Cancelled or no-show: {count}',
        'stf.changes.title': 'What changed',
        'stf.changes.empty': 'No recent changes to your bookings.',
        'stf.changes.generic': 'Booking update',
        'stf.changes.unread': 'New',
        'stf.changes.noLongerYours': 'This booking is no longer assigned to you.',
        'stf.links.label': 'Go to',
        'stf.links.appointments': 'My appointments',
        'stf.links.schedule': 'My schedule',

        /* ── My Schedule (SG-07 / SG-08) ── */
        'stf.schedule.title': 'My schedule',
        'stf.schedule.subtitle': 'Your working hours and time off, as the salon set them. Ask the salon owner if something needs to change.',
        'stf.schedule.loadFailed': 'Couldn’t load your schedule. Check your connection and try again.',
        'stf.schedule.weekly': 'Weekly hours',
        'stf.schedule.weeklyEmpty': 'No working hours have been set for you yet.',
        'stf.schedule.notWorking': 'Not working',
        'stf.schedule.timeOff': 'Upcoming time off',
        'stf.schedule.timeOffEmpty': 'No approved time off coming up.',
        'stf.schedule.fullDay': 'Full day',
        'stf.schedule.everyDayFrom': 'Every day from {start}',
        'stf.schedule.dateRange': '{start} – {end}',
        'stf.schedule.closures': 'Salon closures',
        'stf.schedule.closuresEmpty': 'No salon closures in the coming weeks.',
        'stf.schedule.closed': 'Salon closed',
        'stf.schedule.onlyBranches': 'Only at: {branches}',

        /* ── Booking notifications (SG-16) ── */
        'stf.notif.assigned.title': 'New booking for you',
        'stf.notif.changed.title': 'Booking updated',
        'stf.notif.moved.title': 'Booking moved',
        'stf.notif.removed.title': 'Booking removed from your schedule',
        'stf.notif.cancelled.title': 'Booking cancelled',
        'stf.notif.cancelled.customer': 'Cancelled by the customer',
        'stf.notif.cancelled.salon': 'Cancelled by the salon',
        'stf.notif.cancelled.declined': 'Request declined',
        'stf.notif.cancelled.expired': 'Request expired without confirmation',
        'stf.notif.when': '{date}, {time}',
        'stf.notif.movedFrom': '{now} (was {before})',
        'stf.notif.openNamed': 'Open booking: {title}',
    },
    ar: {
        /* ── Staff booking detail (SG-05) ───────────────────────── */
        'stf.detail.notes': 'ملاحظات الحجز',
        'stf.detail.loadFailed': 'تعذّر تحميل أحدث تفاصيل الحجز. نعرض المعلومات المتوفرة في القائمة.',

        /* ── Deactivation (SG-11) ───────────────────────────────── */
        'stf.deactivate.futureBookingsOne': 'تم إيقاف {name}. لا يزال حجز قادم واحد مُسندًا إلى هذا الموظف — أعد إسناده أو ألغِه من التقويم.',
        'stf.deactivate.futureBookingsMany': 'تم إيقاف {name}. لا تزال حجوزات قادمة مُسندة إلى هذا الموظف (العدد: {count}) — أعد إسنادها أو ألغِها من التقويم.',

        /* ── Login / session (SG-11) ────────────────────────────── */
        'stf.login.accessRemoved': 'تمت إزالة صلاحية وصولك إلى هذا الصالون. تواصل مع صاحب الصالون إذا كنت تعتقد أن هذا خطأ.',

        /* ── Invitation acceptance (SG-10) ──────────────────────── */
        'stf.invite.accountExists': 'يوجد حساب مسجّل بهذا البريد الإلكتروني أو رقم الهاتف، لذا لا يمكن إنشاء حساب جديد بهذه الدعوة. اطلب من صاحب الصالون دعوتك ببيانات تواصل مختلفة.',
        'stf.invite.invalid': 'رابط الدعوة هذا لم يعد صالحًا. ربما انتهت صلاحيته أو سُحبت الدعوة — اطلب من صاحب الصالون إرسال دعوة جديدة.',
        'stf.invite.missingToken': 'رابط الدعوة غير مكتمل. افتح الرابط من رسالة الدعوة مرة أخرى.',
        'stf.invite.acceptFailed': 'تعذّر قبول الدعوة. يُرجى المحاولة مرة أخرى.',

        /* ── S2 (AB#2205) — shared ── */
        'stf.loading': 'جارٍ التحميل…',
        'stf.retry': 'إعادة المحاولة',
        'stf.detail.unavailable': 'لم يعد هذا الحجز متاحًا لك.',
        'stf.absence.other': 'إجازة',
        'stf.nav.myDay': 'يومي',
        'stf.nav.mySchedule': 'جدولي',

        /* ── Identity (SG-17) ── */
        'stf.role.staff': 'موظف',
        'stf.identity.roleAtSalon': '{role} · {salon}',
        'stf.identity.allBranches': 'جميع الفروع',
        'stf.profile.workplace': 'مكان العمل',
        'stf.profile.readOnly': 'يحدّدها صاحب الصالون.',
        'stf.profile.salon': 'الصالون',
        'stf.profile.branches': 'الفروع',
        'stf.profile.position': 'المسمى الوظيفي',
        'stf.profile.role': 'الدور',

        /* ── My Day (SG-06) ── */
        'stf.myDay.greeting': 'مرحبًا {name}',
        'stf.myDay.greetingNoName': 'مرحبًا',
        'stf.myDay.loadFailed': 'تعذّر تحميل يومك. تحقّق من الاتصال وحاول مرة أخرى.',
        'stf.shift.title': 'دوام اليوم',
        'stf.shift.working': 'لديك دوام اليوم',
        'stf.shift.off': 'يوم عطلة',
        'stf.shift.onLeave': 'في إجازة: {type}',
        'stf.shift.awayBetween': 'غير متاح {range}',
        'stf.shift.unknown': 'معلومات دوامك غير متوفرة بعد.',
        'stf.shift.salonClosed': 'الصالون مغلق اليوم',
        'stf.next.title': 'الموعد التالي',
        'stf.next.none': 'لا مواعيد أخرى اليوم.',
        'stf.next.open': 'فتح الحجز',
        'stf.pending.title': 'بانتظار إجراء منك',
        'stf.pending.none': 'لا شيء بانتظارك.',
        'stf.pending.awaitingOne': 'حجز واحد بانتظار التأكيد',
        'stf.pending.awaitingMany': 'حجوزات بانتظار التأكيد: {count}',
        'stf.pending.review': 'مراجعة الطلبات',
        'stf.pending.unpaidOne': 'حجز مكتمل واحد لم يُدفع بعد',
        'stf.pending.unpaidMany': 'حجوزات مكتملة لم تُدفع بعد: {count}',
        'stf.pending.markPaidFor': 'تسجيل الدفع: {time} {service}',
        'stf.pending.seeUnpaid': 'عرض الحجوزات غير المدفوعة',
        'stf.today.title': 'مواعيد اليوم',
        'stf.today.empty': 'لا مواعيد لديك اليوم.',
        'stf.today.nextTag': 'التالي',
        'stf.today.openNamed': 'فتح حجز الساعة {time}: {service}',
        'stf.counts.label': 'ملخص اليوم',
        'stf.counts.pending': 'بانتظار التأكيد: {count}',
        'stf.counts.confirmed': 'مؤكد: {count}',
        'stf.counts.inVisit': 'وصل أو بدأ: {count}',
        'stf.counts.completed': 'مكتمل: {count}',
        'stf.counts.cancelled': 'ملغى أو لم يحضر: {count}',
        'stf.changes.title': 'آخر التغييرات',
        'stf.changes.empty': 'لا تغييرات حديثة على حجوزاتك.',
        'stf.changes.generic': 'تحديث على حجز',
        'stf.changes.unread': 'جديد',
        'stf.changes.noLongerYours': 'لم يعد هذا الحجز مُسندًا إليك.',
        'stf.links.label': 'انتقل إلى',
        'stf.links.appointments': 'مواعيدي',
        'stf.links.schedule': 'جدولي',

        /* ── My Schedule (SG-07 / SG-08) ── */
        'stf.schedule.title': 'جدولي',
        'stf.schedule.subtitle': 'ساعات عملك وإجازاتك كما حدّدها الصالون. تواصل مع صاحب الصالون إذا احتجت إلى أي تعديل.',
        'stf.schedule.loadFailed': 'تعذّر تحميل جدولك. تحقّق من الاتصال وحاول مرة أخرى.',
        'stf.schedule.weekly': 'ساعات العمل الأسبوعية',
        'stf.schedule.weeklyEmpty': 'لم تُحدَّد ساعات عمل لك بعد.',
        'stf.schedule.notWorking': 'لا دوام',
        'stf.schedule.timeOff': 'الإجازات القادمة',
        'stf.schedule.timeOffEmpty': 'لا إجازات معتمدة قادمة.',
        'stf.schedule.fullDay': 'يوم كامل',
        'stf.schedule.everyDayFrom': 'يوميًا ابتداءً من {start}',
        'stf.schedule.dateRange': 'من {start} إلى {end}',
        'stf.schedule.closures': 'أيام إغلاق الصالون',
        'stf.schedule.closuresEmpty': 'لا أيام إغلاق للصالون في الأسابيع القادمة.',
        'stf.schedule.closed': 'الصالون مغلق',
        'stf.schedule.onlyBranches': 'فقط في: {branches}',

        /* ── Booking notifications (SG-16) ── */
        'stf.notif.assigned.title': 'حجز جديد لك',
        'stf.notif.changed.title': 'تم تحديث الحجز',
        'stf.notif.moved.title': 'تمت إعادة جدولة الحجز',
        'stf.notif.removed.title': 'تمت إزالة الحجز من جدولك',
        'stf.notif.cancelled.title': 'تم إلغاء الحجز',
        'stf.notif.cancelled.customer': 'ألغاه العميل',
        'stf.notif.cancelled.salon': 'ألغاه الصالون',
        'stf.notif.cancelled.declined': 'رفضه الصالون',
        'stf.notif.cancelled.expired': 'انتهت صلاحية الطلب دون تأكيد',
        'stf.notif.when': '{date}، {time}',
        'stf.notif.movedFrom': '{now} (سابقًا: {before})',
        'stf.notif.openNamed': 'فتح الحجز: {title}',
    },
};
